<script setup lang="ts">
import { computed, ref } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryScroller> in <template>
import ClassicPortraitGalleryScroller from './ClassicPortraitGalleryScroller.vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryTile> in <template>
import ClassicPortraitGalleryTile from './ClassicPortraitGalleryTile.vue';
import {
  alternate,
  type ClassicPortraitGalleryPlan,
  type GalleryEntry,
  galleryTileBindings,
  type MixedPortraitGalleryItem,
  mixedItemRatio,
  packMixedGalleryRows,
} from './classic-mixed-portrait-gallery';

interface Props {
  activeUuid?: string | null;
  entries: GalleryEntry[];
  generateMoreDisabled?: boolean;
  getSelectedPortraitCount: (
    item: MixedPortraitGalleryItem
  ) => number | undefined;
  plan: ClassicPortraitGalleryPlan;
  selectedUuids: string[];
  selectionOnTile?: boolean;
  showGenerateMore?: boolean;
}

interface FeedRow {
  entries: GalleryEntry[];
  // The widths of the tiles in row heights, summed: the row's height is
  // the width it has over this.
  units: number;
}

interface ScrollTarget {
  canScroll: () => boolean;
  isItemVisible: (uuid: string) => boolean | null;
  scrollToItem: (uuid: string) => boolean;
}

// A row holds `feedColumns` vertical portraits, with a little slack so a
// 3:2 group and two singles share a row of four.
const ROW_SLACK = 0.4;

const props = withDefaults(defineProps<Props>(), {
  activeUuid: null,
  generateMoreDisabled: false,
  selectionOnTile: true,
  showGenerateMore: false,
});
const emit = defineEmits<{
  activate: [item: MixedPortraitGalleryItem, index: number, event: MouseEvent];
  countDecrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  countIncrement: [event: MouseEvent, item: MixedPortraitGalleryItem];
  generateMore: [];
  itemLoad: [item: MixedPortraitGalleryItem, index: number, event: Event];
  switchSlide: [uuids: string[]];
  tryAgain: [item: MixedPortraitGalleryItem];
}>();
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const { tileBindings, tileKey } = galleryTileBindings(props, emit);

const scroller = ref<ScrollTarget | null>(null);

function unitsOf(entry: GalleryEntry): number {
  return mixedItemRatio(entry.item) / props.plan.rail.portraitRatio;
}

// Justified rows: a group portrait opens a row and singles fill in behind
// it when there is room, the rest of the singles pack into rows of their
// own, and the two kinds of row alternate down the feed.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const rows = computed<FeedRow[]>(() => {
  const packed = packMixedGalleryRows(
    props.entries,
    unitsOf,
    props.plan.feedColumns + ROW_SLACK,
    (entry) => entry.item.kind === 'group'
  );
  const ledByGroup = packed.filter(
    (row) => row.items[0]?.item.kind === 'group'
  );
  const singles = packed.filter((row) => row.items[0]?.item.kind !== 'group');
  return alternate(ledByGroup, singles).map((row) => ({
    entries: row.items,
    units: row.items.reduce(
      (sum, entry) => sum + mixedItemRatio(entry.item),
      0
    ),
  }));
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const gridStyle = computed(() => ({
  '--cmg-feed-columns': String(props.plan.feedColumns),
  '--cmg-feed-portrait-ratio': String(props.plan.rail.portraitRatio),
}));

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function rowStyle(row: FeedRow): Record<string, string> {
  return {
    '--cmg-feed-row-gaps': String(Math.max(0, row.entries.length - 1)),
    '--cmg-feed-row-units': String(row.units || 1),
  };
}

function canScroll(): boolean {
  return scroller.value?.canScroll() ?? false;
}

function isItemVisible(uuid: string): boolean | null {
  return scroller.value?.isItemVisible(uuid) ?? null;
}

function scrollToItem(uuid: string): boolean {
  return scroller.value?.scrollToItem(uuid) ?? false;
}

defineExpose({ canScroll, isItemVisible, scrollToItem });
</script>

<template>
  <ClassicPortraitGalleryScroller
    ref="scroller"
    class="ClassicMixedPortraitGallery__feed"
    data-testid="classic-mixed-gallery-feed"
    axis="y"
    :snap="false"
    :item-count="entries.length"
    @visible-items-change="emit('switchSlide', $event)"
  >
    <div class="ClassicMixedPortraitGallery__feed-grid" :style="gridStyle">
      <div
        v-for="(row, rowIndex) in rows"
        :key="rowIndex"
        class="ClassicMixedPortraitGallery__feed-row"
        :style="rowStyle(row)"
      >
        <ClassicPortraitGalleryTile
          v-for="entry in row.entries"
          :key="tileKey(entry)"
          v-bind="tileBindings(entry)"
        />
      </div>
      <button
        v-if="showGenerateMore"
        class="ClassicMixedPortraitGallery__more-row"
        type="button"
        data-testid="gallery-generate-more"
        :disabled="generateMoreDisabled"
        :aria-label="$t('PortraitsGroup.buttonMore')"
        @click="emit('generateMore')"
      >
        +
      </button>
    </div>
  </ClassicPortraitGalleryScroller>
</template>

<style scoped lang="scss">
.ClassicMixedPortraitGallery__feed {
  min-height: 0;
}

/* The bottom padding keeps the last row clear of the edge fade. */
.ClassicMixedPortraitGallery__feed-grid {
  --cmg-feed-inset: 0.3rem;
  --cmg-feed-width: calc(100cqw - 2 * var(--cmg-feed-inset));

  display: flex;
  flex-direction: column;
  gap: var(--cmg-gap);
  box-sizing: border-box;
  width: 100%;
  padding: var(--cmg-feed-inset) var(--cmg-feed-inset) 4rem;
}

/* A row's height is the width it has over the widths its tiles need, so
   the row is level and fills the width; a row with room to spare (the
   last one, or a lone tile) is capped at the height a full row of
   portraits would get, and sits in the middle. The cqw here resolves
   against the scroll viewport, on the tile that reads the variable. */
.ClassicMixedPortraitGallery__feed-row {
  --cmg-tile-max-height: min(
    calc(
      (var(--cmg-feed-width) - var(--cmg-feed-row-gaps) * var(--cmg-gap)) /
        var(--cmg-feed-row-units)
    ),
    calc(
      (var(--cmg-feed-width) - (var(--cmg-feed-columns) - 1) * var(--cmg-gap)) /
        var(--cmg-feed-columns) / var(--cmg-feed-portrait-ratio)
    )
  );

  display: flex;
  gap: var(--cmg-gap);
  justify-content: center;
  min-width: 0;
}

.ClassicMixedPortraitGallery__more-row {
  display: grid;
  place-items: center;
  height: calc(var(--cmg-touch) * 1.4);
  font-size: 2.6rem;
  line-height: 1;
  color: var(--Kiosk--tint-inverse-color);
  cursor: pointer;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.18);
  border: 2px dashed rgba(var(--Kiosk--tint-inverse-color-rgb), 0.5);
  border-radius: var(--cmg-radius, 0.8rem);
  outline: none;
}

.ClassicMixedPortraitGallery__more-row:disabled {
  cursor: default;
  opacity: 0.4;
}
</style>
